import { useCallback, useEffect, useState } from "react";
import { accountService, homeService } from "@miplatform/api";
import { AutocompleteOption } from "@miplatform/components";
import {
  ATTEMPT_STATUSES,
  HTTP_STATUSES,
  MAX_PAGE_SIZE,
} from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  AccountType,
  IAccountResponse,
  IAttempt,
  IAttemptListItemResponseForWorkOrder,
  ICommunityListItemResponse,
} from "@miplatform/types";
import { isSame } from "@miplatform/utils";

const STATUS_OPTIONS: AutocompleteOption[] = [
  { label: "New", value: ATTEMPT_STATUSES.NEW },
  // TODO: AB: Unable to Auto-Assign is not a valid status, we need to add it to the API
  { label: "Unable to Auto-Assign", value: ATTEMPT_STATUSES.PENDING },
  { label: "Complete", value: ATTEMPT_STATUSES.SUBMITTED },
  { label: "On Hold", value: ATTEMPT_STATUSES.HOLD },
  { label: "Late", value: ATTEMPT_STATUSES.PLANNED },
];

export const useCalendarFilterGroup = () => {
  const stateManager = useComponentState();
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<CalendarFilters>({
    status: null,
    builder: null,
    community: null,
    dueDate: null,
  });
  const [appliedFilters, setAppliedFilters] = useState<CalendarFilters>({
    status: null,
    builder: null,
    community: null,
    dueDate: null,
  });
  const [builderOptions, setBuilderOptions] = useState<AutocompleteOption[]>(
    []
  );
  const [communityOptions, setCommunityOptions] = useState<
    AutocompleteOption[]
  >([]);

  const loadOptions = () => {
    stateManager.run(async () => {
      const [buildersResponse, communitiesResponse] = await Promise.all([
        accountService.accounts.getAll({
          sort: "name",
          top: MAX_PAGE_SIZE,
          filters: {
            accountType: AccountType.Builder,
          },
        }),
        homeService.communities.getAll({
          sort: "name",
        }),
      ]);

      if (buildersResponse.status === HTTP_STATUSES.OK) {
        setBuilderOptions(
          (buildersResponse.data as IAccountResponse[]).map((customer) => ({
            label: customer.name,
            value: customer.id,
          }))
        );
      }

      if (communitiesResponse.status === HTTP_STATUSES.OK) {
        setCommunityOptions(
          (communitiesResponse.data as ICommunityListItemResponse[]).map(
            (community) => ({
              label: community.name,
              value: community.id,
            })
          )
        );
      }
    });
  };

  useEffect(() => {
    loadOptions();
  }, []);

  const toggleFilters = () => setShowFilters((prev) => !prev);

  const handleFilterChange = useCallback(
    <K extends keyof CalendarFilters>(key: K, value: CalendarFilters[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    [setFilters]
  );

  const appliedFilterLabels = [
    appliedFilters.status?.label,
    appliedFilters.builder?.label,
    appliedFilters.community?.label,
  ].filter(Boolean) as string[];

  const hasPendingFilters =
    !!filters.status ||
    !!filters.builder ||
    !!filters.community ||
    !!filters.dueDate ||
    appliedFilterLabels.length > 0;

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    toggleFilters();
  };

  const handleResetFilters = () => {
    const emptyFilters: CalendarFilters = {
      status: null,
      builder: null,
      community: null,
      dueDate: null,
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
  };

  return {
    showFilters,
    toggleFilters,
    filters,
    setFilters,
    appliedFilters,
    setAppliedFilters,
    appliedFilterLabels,
    hasPendingFilters,
    handleApplyFilters,
    handleResetFilters,
    builderOptions,
    communityOptions,
    statusOptions: STATUS_OPTIONS,
    isLoading: stateManager.isBusy(),
    handleFilterChange,
  };
};

export type CalendarFilterGroupController = ReturnType<
  typeof useCalendarFilterGroup
>;

export type CalendarFilters = {
  status: AutocompleteOption | null;
  builder: AutocompleteOption | null;
  community: AutocompleteOption | null;
  dueDate: moment.Moment | null;
};

export type GhostableAttempt<T extends IAttempt = IAttempt> = T & {
  isGhost?: boolean;
};

export const matchesCalendarFilters = (
  event: IAttemptListItemResponseForWorkOrder,
  filters: CalendarFilters
): boolean => {
  const { status, builder, community, dueDate } = filters;

  if (status?.value && event.attemptStatus !== status.value) {
    return false;
  }
  if (builder?.value && event.home?.builderId !== builder.value) {
    return false;
  }
  if (community?.value && event.home?.communityId !== community.value) {
    return false;
  }
  if (dueDate) {
    const hardDueDate = event.assignment?.workOrder?.hardDueDate;

    if (
      !hardDueDate ||
      !isSame({ date1: hardDueDate, date2: dueDate, timeUnit: "day" })
    ) {
      return false;
    }
  }
  return true;
};

/**
 * Returns every event passed in, annotating those that do NOT match the
 * active filters with `isGhost: true` so consumers can render them in a
 * dimmed/ghost state instead of hiding them completely.
 */
export const applyCalendarFilters = <T extends IAttempt>(
  events: T[],
  filters: CalendarFilters
): GhostableAttempt<T>[] => {
  const { status, builder, community, dueDate } = filters;
  const hasActiveFilter = !!status || !!builder || !!community || !!dueDate;

  if (!hasActiveFilter) {
    return events.map((event) => ({ ...event, isGhost: false }));
  }

  return events.map((event) => ({
    ...event,
    isGhost: !matchesCalendarFilters(event, filters),
  }));
};
